import { useState } from "react";
import axios from "axios";
import { X, Sparkles, ArrowRight, AlertTriangle, CheckCircle2, Target } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const cleanMd = (t) => t ? t.replace(/\*\*/g, '').replace(/\*/g, '').replace(/#{1,3}\s/g, '') : t;

export default function ActionPlaybook({ isOpen, onClose, token, period, refDate, insight }) {
  const [playbook, setPlaybook] = useState(null);
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    if (loading || !insight) return;
    setLoading(true);
    setPlaybook(null);

    const prompt = `You are a real estate industry strategist with 20 years of experience in Indian residential real estate.

Context: ${insight.title || insight.type || ''} — ${insight.detail || insight.insight || insight.message || ''}
Project: ${insight.project || 'Portfolio-wide'}
Metric: ${insight.metric || ''}

Generate a concise ACTION PLAYBOOK. Use real estate industry expertise. Format as JSON:
{
  "diagnosis": "What exactly is happening and why — 2 lines max",
  "impact": "Business consequence if not addressed — quantify in Cr/units",
  "actions": [
    {"step": "Specific action 1", "timeline": "This week / This month", "expected_outcome": "What will improve and by how much"},
    {"step": "Specific action 2", "timeline": "...", "expected_outcome": "..."},
    {"step": "Specific action 3", "timeline": "...", "expected_outcome": "..."}
  ],
  "industry_benchmark": "What top-performing real estate companies do in this situation — 1 line",
  "risk_if_ignored": "What happens in 3 months if no action taken — be specific with numbers"
}

Rules:
- Use Indian real estate context (payment plans, OC delays, RERA, NRI buyers, channel partners)
- Be specific to residential realty — not generic business advice
- Keep each field to 1-2 lines max
- Use Cr/L for amounts`;

    try {
      const res = await axios.post(`${API}/ai/copilot`, {
        message: prompt,
        session_id: `playbook-${Date.now()}`,
        period, ref_date: refDate,
      }, { headers: { Authorization: `Bearer ${token}` } });

      let text = res.data.response || '';
      text = text.replace(/```json\s*/g, '').replace(/```\s*/g, '').trim();
      try {
        setPlaybook(JSON.parse(text));
      } catch {
        setPlaybook({ diagnosis: cleanMd(text.slice(0, 300)), impact: '', actions: [], industry_benchmark: '', risk_if_ignored: '' });
      }
    } catch {
      setPlaybook({ diagnosis: 'Unable to generate playbook.', impact: '', actions: [], industry_benchmark: '', risk_if_ignored: '' });
    } finally { setLoading(false); }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4" data-testid="action-playbook-modal">
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-lg max-h-[92vh] sm:max-h-[85vh] overflow-y-auto z-10 pb-safe">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-100 px-5 py-4 rounded-t-2xl flex items-center justify-between">
          <div>
            <p className="text-xs font-bold text-blue-600 uppercase tracking-wider">Action Playbook</p>
            <p className="text-sm font-bold text-slate-900 mt-0.5" style={{ fontFamily: 'Manrope' }}>{insight?.project || insight?.title || insight?.type || 'Insight'}</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg"><X className="h-4 w-4 text-slate-500" /></button>
        </div>

        <div className="px-5 py-4">
          {/* Context */}
          <div className="mb-4 p-3 bg-slate-50 rounded-xl">
            <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Context</p>
            <p className="text-xs text-slate-700">{insight?.detail || insight?.insight || insight?.message || '--'}</p>
            {insight?.metric && <p className="text-xs font-semibold text-slate-900 mt-1">{insight.metric}</p>}
          </div>

          {!playbook && !loading && (
            <button onClick={generate} className="w-full flex items-center justify-center gap-2 py-3 bg-[#1D4ED8] hover:bg-[#1E40AF] text-white rounded-xl font-semibold text-sm transition-colors" data-testid="generate-playbook-btn">
              <Sparkles className="h-4 w-4" /> Generate Action Playbook
            </button>
          )}

          {loading && (
            <div className="flex items-center justify-center gap-2 py-8 text-blue-600">
              <div className="h-4 w-4 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
              <span className="text-sm font-medium">Analyzing with industry intelligence...</span>
            </div>
          )}

          {playbook && (
            <div className="space-y-4">
              {/* Diagnosis */}
              {playbook.diagnosis && (
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Diagnosis</p>
                  <p className="text-sm text-slate-800 leading-relaxed">{cleanMd(playbook.diagnosis)}</p>
                </div>
              )}

              {/* Impact */}
              {playbook.impact && (
                <div className="p-3 bg-red-50 rounded-xl border border-red-100">
                  <div className="flex items-center gap-1.5 mb-1"><AlertTriangle className="h-3.5 w-3.5 text-red-500" /><p className="text-[10px] font-bold text-red-600 uppercase">Business Impact</p></div>
                  <p className="text-xs text-red-800 leading-relaxed">{cleanMd(playbook.impact)}</p>
                </div>
              )}

              {/* Actions */}
              {playbook.actions?.length > 0 && (
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Action Steps</p>
                  <div className="space-y-2">
                    {playbook.actions.map((a, i) => (
                      <div key={i} className="p-3 bg-blue-50 rounded-xl border border-blue-100">
                        <div className="flex items-start gap-2">
                          <div className="h-5 w-5 rounded-full bg-[#1D4ED8] flex items-center justify-center flex-shrink-0 mt-0.5"><span className="text-[10px] font-bold text-white">{i + 1}</span></div>
                          <div className="flex-1">
                            <p className="text-xs font-semibold text-slate-900">{cleanMd(a.step)}</p>
                            <div className="flex items-center gap-3 mt-1">
                              <span className="text-[10px] text-blue-600 font-medium">{a.timeline}</span>
                              <ArrowRight className="h-2.5 w-2.5 text-slate-300" />
                              <span className="text-[10px] text-emerald-600 font-medium">{cleanMd(a.expected_outcome)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Industry Benchmark */}
              {playbook.industry_benchmark && (
                <div className="p-3 bg-emerald-50 rounded-xl border border-emerald-100">
                  <div className="flex items-center gap-1.5 mb-1"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /><p className="text-[10px] font-bold text-emerald-600 uppercase">Industry Best Practice</p></div>
                  <p className="text-xs text-emerald-800 leading-relaxed">{cleanMd(playbook.industry_benchmark)}</p>
                </div>
              )}

              {/* Risk if Ignored */}
              {playbook.risk_if_ignored && (
                <div className="p-3 bg-amber-50 rounded-xl border border-amber-100">
                  <div className="flex items-center gap-1.5 mb-1"><Target className="h-3.5 w-3.5 text-amber-500" /><p className="text-[10px] font-bold text-amber-600 uppercase">Risk if Ignored (3 months)</p></div>
                  <p className="text-xs text-amber-800 leading-relaxed">{cleanMd(playbook.risk_if_ignored)}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
